import React, { useState, useEffect} from 'react';
import { supabase } from './lib/supabaseClient.js';
import Dashboard from './components/Dashboard.jsx';
import ComplianceChatbot from './components/ComplianceChatbot.jsx';
import DocumentGenerator from './components/DocumentGenerator.jsx';
import WelcomeScreen from './components/WelcomeScreen.jsx';
// import NotFound from './components/NotFound';
import CrossFrameworkMapper from './components/CrossFrameworkMapper.jsx';
import RiskCalculator from './components/RiskCalculator.jsx';
import EUAIActClassifier from './components/EUAIActClassifier.jsx';
import ChecklistBuilder from './components/ChecklistBuilder.jsx';
import { ProgressJourneyBar, OnboardingTooltip } from './components/AppEnhancements.jsx';
import {  initUserEmail,  migrateLegacyKeys,  syncFromServer,  startSync,  stopSync,  userKey,} from'./utils/storageHelper.js';
import SettingsPage from './components/SettingsPage.jsx';
import { getAccountStatus } from './lib/trial.js';
import UpgradeScreen from './components/UpgradeScreen.jsx';
import TrialBanner from './components/TrialBanner.jsx';
import AdminPanel from './components/AdminPanel.jsx';
import { EUFlagIcon } from './components/EUFlagIcon.jsx';

// Shared with AdminPanel.jsx's pattern -- dev hits vercel dev on :3000, prod is same-origin
const API_URL = process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : '';


        const successBlinkStyle = `
        @keyframes blinkSuccess {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.35; }
        }
        `;

        if (typeof document !== 'undefined' && !document.getElementById('blink-success-style')) {
            const styleTag = document.createElement('style');
            styleTag.id = 'blink-success-style';
            styleTag.innerHTML = successBlinkStyle;
            document.head.appendChild(styleTag);
        }

// ── Eye / Eye-off icon for password show/hide toggles ──
// Industry-standard stroke SVG icon (matches GitHub/Stripe/Google conventions)
// instead of emoji, which render inconsistently in size across OS/browsers.
function EyeIcon({ open }) {
  return open ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// ── Social Login Dropdown Component ──
function SocialLoginDropdown({ onGoogle, onGithub, googleLoading, githubLoading }) {
  const [open, setOpen] = React.useState(false);
  const anyLoading = googleLoading || githubLoading;

  return (
    <div style={{ position: 'relative', marginBottom: '4px' }}>
      {/* Trigger Button */}
      <button
        onClick={() => !anyLoading && setOpen(prev => !prev)}
        disabled={anyLoading}
        style={{
          width: '100%',
          padding: '14px',
          backgroundColor: anyLoading ? '#f1f1f1' : 'white',
          color: '#444',
          border: '2px solid #dadce0',
          borderRadius: open ? '12px 12px 0 0' : '12px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: anyLoading ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(4px, 0.6vw, 10px)' }}>
          {/* Google mini icon */}
          <svg width="16" height="16" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          {/* GitHub mini icon */}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#333">
            <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z"/>
          </svg>
          <span>{anyLoading ? 'Redirecting...' : 'Continue with Social Account'}</span>
        </div>
        <span style={{ fontSize: '12px', color: '#999', transition: 'transform 0.2s', display: 'inline-block', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
      </button>

      {/* Dropdown Menu */}
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '2px solid #dadce0',
          borderTop: 'none',
          borderRadius: '0 0 12px 12px',
          zIndex: 100,
          overflow: 'hidden',
          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
        }}>
          {/* Google Option */}
          <button
            onClick={() => { setOpen(false); onGoogle(); }}
            disabled={googleLoading}
            style={{
              width: '100%',
              padding: '14px 18px',
              backgroundColor: 'white',
              color: '#444',
              border: 'none',
              borderBottom: '1px solid #f0f0f0',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              textAlign: 'left',
              boxSizing: 'border-box',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
          >
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            <div>
              <div style={{ fontWeight: '600', color: '#333' }}>Continue with Google</div>
              <div style={{ fontSize: '12px', color: '#999' }}>Uses your signed-in Google account</div>
            </div>
          </button>

          {/* GitHub Option */}
          <button
            onClick={() => { setOpen(false); onGithub(); }}
            disabled={githubLoading}
            style={{
              width: '100%',
              padding: '14px 18px',
              backgroundColor: 'white',
              color: '#444',
              border: 'none',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
              textAlign: 'left',
              boxSizing: 'border-box',
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#333">
              <path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61-.546-1.385-1.335-1.755-1.335-1.755-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 21.795 24 17.295 24 12c0-6.63-5.37-12-12-12z"/>
            </svg>
            <div>
              <div style={{ fontWeight: '600', color: '#333' }}>Continue with GitHub</div>
              <div style={{ fontSize: '12px', color: '#999' }}>Uses your GitHub account</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

// Beautiful Login Component (cyan & blue)
function LoginPage({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isSignup, setIsSignup] = useState(false);
  const [signupComplete, setSignupComplete] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [githubLoading, setGithubLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [hcaptchaScriptLoaded, setHcaptchaScriptLoaded] = useState(false);
  const captchaRef = React.useRef(null);

  // Check if URL contains password reset token
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');

    if (type === 'recovery' && accessToken) {
      setIsResetMode(true);
      setMessage('Please enter your new password below');
    }
  }, []);

  // Password validation rules
  const passwordRules = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const resetPasswordRules = {
    minLength: newPassword.length >= 8,
    hasUpperCase: /[A-Z]/.test(newPassword),
    hasLowerCase: /[a-z]/.test(newPassword),
    hasNumber: /[0-9]/.test(newPassword),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
  };

  const isPasswordValid = Object.values(passwordRules).every(rule => rule);
  const isResetPasswordValid = Object.values(resetPasswordRules).every(rule => rule);

  // Load hCaptcha script
  useEffect(() => {
    const existingScripts = document.querySelectorAll('script[src*="hcaptcha"]');
    existingScripts.forEach(script => script.remove());

    const script = document.createElement('script');
    script.src = 'https://js.hcaptcha.com/1/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      console.log('hCaptcha script loaded successfully');
      setHcaptchaScriptLoaded(true);
    };
    script.onerror = () => {
      console.error('Failed to load hCaptcha script');
    };
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.querySelector('script[src*="hcaptcha"]');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, []);

  // Render hCaptcha widget
  useEffect(() => {
    if (isSignup && hcaptchaScriptLoaded && captchaRef.current) {
      const timer = setTimeout(() => {
        if (window.hcaptcha && captchaRef.current) {
          try {
            window.hcaptcha.render(captchaRef.current, {
              sitekey: 'be744c4c-ba9a-429e-9056-006c3c9e915e',
              callback: (token) => {
                console.log('CAPTCHA completed');
                setCaptchaToken(token);
              },
              theme: "light"
            });
          } catch (error) {
            console.error('hCaptcha render error:', error);
          }
        }
      }, 800);

      return () => {
        clearTimeout(timer);
        if (window.hcaptcha) {
           window.hcaptcha.reset();
           setCaptchaToken('');
        }
      };
    }
  }, [isSignup, hcaptchaScriptLoaded]);

  // === GOOGLE SIGN IN ===
  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setMessage('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });
    if (error) {
      setMessage('Google sign-in failed: ' + error.message);
      setGoogleLoading(false);
    }
  };

  // === GITHUB SIGN IN ===
  const handleGithubLogin = async () => {
    setGithubLoading(true);
    setMessage('');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'github',
      options: { redirectTo: 'https://dashboard.quantumaishield.com/dashboard' },
    });
    if (error) {
      setMessage('GitHub sign-in failed: ' + error.message);
      setGithubLoading(false);
    }
  };

  const handleSignup = async () => {
    if (!username || username.trim() === '') {
      setMessage('Please enter a username');
      return;
    }

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setMessage('Please enter a valid email address');
      return;
    }

    if (!isPasswordValid) {
      setMessage('Password does not meet complexity requirements');
      setShowPasswordRules(true);
      return;
    }

    if (!captchaToken) {
      setMessage('⬆️ Please complete the CAPTCHA verification above');
      return;
    }

    setLoading(true);
    setMessage('Creating account...');
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { username },
        emailRedirectTo: window.location.origin,
        captchaToken: captchaToken,
      },
    });


          if (error) {
            setMessage(error.message);
          } else {
            setSignupComplete(true);
             setMessage('');
          }
          setLoading(false);

          if (window.hcaptcha) {
            window.hcaptcha.reset();
            setCaptchaToken('');
          }
        };


const handleLogin = async () => {
    setLoading(true);
    setMessage('Logging in...');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      if (error.message === 'Email not confirmed') {
        setMessage('Please confirm your email first — check your inbox and spam/junk folder for the link (valid 24 hours, one-time use), or click below to resend it.');
        setShowResend(true);
      } else {
        setMessage(error.message);
        setShowResend(false);
      }
    } else {
      onLogin(data.user);
    }
    setLoading(false);
  };

        const handleResendConfirmation = async () => {
          if (!email) return setMessage('Enter your email above first');
          if (resendCooldown) return;

          setLoading(true);
          setResendCooldown(true);
          setMessage('Resending confirmation email...');
          const { error } = await supabase.auth.resend({ type: 'signup', email });
          setMessage(error ? error.message : 'Confirmation email resent — check your inbox and spam folder.');
          setLoading(false);

          setTimeout(() => setResendCooldown(false), 60000);
        };


  const handleForgot = async () => {
    if (!email) return setMessage('Type your email first!');
    setMessage('Sending reset link...');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}`,
    });
    setMessage(error ? error.message : 'Reset link sent! Check your email 📧');
  };

  const handlePasswordReset = async () => {
    if (!isResetPasswordValid) {
      setMessage('Password does not meet complexity requirements');
      return;
    }

    setLoading(true);
    setMessage('Updating password...');
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      setMessage(error.message);
      setLoading(false);
    } else {
      setMessage('✅ Password updated successfully! Redirecting to login...');
      setTimeout(() => {
        supabase.auth.signOut();
        setIsResetMode(false);
        setNewPassword('');
        window.location.href = window.location.origin;
      }, 2000);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to right, #0ff0fc, #2a6cff)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontFamily: "Inter, Arial, sans-serif"
    }}>
      <div style={{
        background: 'white',
        padding: '50px 40px',
        borderRadius: '24px',
        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
        width: '380px',
        textAlign: 'center'
      }}>
        <h1 style={{ color: '#0088cc', marginBottom: '10px' }}>Quantum AI Shield 🛡️</h1>
        <p style={{ color: '#666', marginBottom: '30px' }}>AI GRC Platform with Quantum-Safe Audit Trails</p>

        {/* ── Google Sign In Button ── */}
        {!isResetMode && (
          <>
            <SocialLoginDropdown
              onGoogle={handleGoogleLogin}
              onGithub={handleGithubLogin}
              googleLoading={googleLoading}
              githubLoading={githubLoading}
            />

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e5e5' }} />
              <span style={{ color: '#999', fontSize: '13px', whiteSpace: 'nowrap' }}>or continue with email</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e5e5' }} />
            </div>
          </>
        )}

        {isResetMode ? (
          <>
            <h2 style={{ color: '#0088cc' }}>Reset Password</h2>
            <div style={{ position: 'relative', margin: '10px 0' }}>
              <input
                style={{ ...s.input, margin: 0, paddingRight: '44px', boxSizing: 'border-box' }}
                type={showNewPassword ? 'text' : 'password'}
                placeholder="New Password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                onFocus={() => setShowPasswordRules(true)}
              />
              <button type="button" onClick={() => setShowNewPassword(p => !p)} aria-label={showNewPassword ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, margin: 0, lineHeight: 1, width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <EyeIcon open={showNewPassword} />
              </button>
            </div>

            {showPasswordRules && (
              <div style={{
                textAlign: 'left',
                padding: '15px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                marginTop: '10px',
                fontSize: '14px'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#0088cc' }}>
                  Password Requirements:
                </div>
                <div style={{ color: resetPasswordRules.minLength ? '#22c55e' : '#94a3b8' }}>
                  {resetPasswordRules.minLength ? '✓' : '○'} At least 8 characters
                </div>
                <div style={{ color: resetPasswordRules.hasUpperCase ? '#22c55e' : '#94a3b8' }}>
                  {resetPasswordRules.hasUpperCase ? '✓' : '○'} One uppercase letter (A-Z)
                </div>
                <div style={{ color: resetPasswordRules.hasLowerCase ? '#22c55e' : '#94a3b8' }}>
                  {resetPasswordRules.hasLowerCase ? '✓' : '○'} One lowercase letter (a-z)
                </div>
                <div style={{ color: resetPasswordRules.hasNumber ? '#22c55e' : '#94a3b8' }}>
                  {resetPasswordRules.hasNumber ? '✓' : '○'} One number (0-9)
                </div>
                <div style={{ color: resetPasswordRules.hasSpecialChar ? '#22c55e' : '#94a3b8' }}>
                  {resetPasswordRules.hasSpecialChar ? '✓' : '○'} One special character (!@#$%^&*)
                </div>
              </div>
            )}

            <button style={s.btn} onClick={handlePasswordReset} disabled={loading || !isResetPasswordValid}>
              Update Password 🔐
            </button>
            <p>
              <span onClick={() => { setIsResetMode(false); window.location.href = window.location.origin; }} style={s.link}>
                Back to Login
              </span>
            </p>
          </>


          ) : !isSignup ? (
          <>
            <h2 style={{ color: '#0088cc' }}>Login</h2>
            <input style={{ ...s.input, width: '92%' }} type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
            <div style={{ position: 'relative', margin: '10px 0' }}>
              <input style={{ ...s.input, margin: 0, paddingRight: '44px', boxSizing: 'border-box' }} type={showPassword ? 'text' : 'password'} placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} />
              <button type="button" onClick={() => setShowPassword(p => !p)} aria-label={showPassword ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, margin: 0, lineHeight: 1, width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <EyeIcon open={showPassword} />
              </button>
            </div>
            <button style={s.btn} onClick={handleLogin} disabled={loading}>Login 🚀</button>
            <p style={{ margin: '10px 0' }}>New here? <span onClick={() => setIsSignup(true)} style={s.link}>Create account</span></p>
            <p style={{ margin: '10px 0' }}>
              <span onClick={handleForgot} style={{ color: '#00aaff', cursor: 'pointer', fontWeight: 'bold' }}>
                Forgot Password? 🔑
              </span>
            </p>
          </>
        ) : signupComplete ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
            <h2 style={{ color: '#22c55e', marginBottom: '10px' }}>Account created!</h2>
            <p style={{ color: '#666', marginBottom: '8px' }}>
              Check your email — including your spam/junk folder — and click the confirmation link to verify your account.
            </p>
            <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '24px' }}>
              This link is valid for 24 hours and can only be used once, so please open it directly rather than forwarding the email.
            </p>
            <button
              style={s.btn}
              onClick={() => { setSignupComplete(false); setIsSignup(false); setMessage(''); }}
            >
              Back to Login
            </button>
          </div>
        ) : (
          <>
            <h2 style={{ color: '#0088cc' }}>Create Account</h2>
                        <input style={{ ...s.input, width: '92%' }} type="text" placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} />
                        <input style={{ ...s.input, width: '92%' }} type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} />
            <div style={{ position: 'relative', margin: '10px 0' }}>
              <input
                style={{ ...s.input, margin: 0, paddingRight: '44px', boxSizing: 'border-box' }}
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onFocus={() => setShowPasswordRules(true)}
              />
              <button type="button" onClick={() => setShowPassword(p => !p)} aria-label={showPassword ? 'Hide password' : 'Show password'} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, margin: 0, lineHeight: 1, width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <EyeIcon open={showPassword} />
              </button>
            </div>

            {showPasswordRules && isSignup && (
              <div style={{
                textAlign: 'left',
                padding: '15px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                marginTop: '10px',
                fontSize: '14px'
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#0088cc' }}>
                  Password Requirements:
                </div>
                <div style={{ color: passwordRules.minLength ? '#22c55e' : '#94a3b8' }}>
                  {passwordRules.minLength ? '✓' : '○'} At least 8 characters
                </div>
                <div style={{ color: passwordRules.hasUpperCase ? '#22c55e' : '#94a3b8' }}>
                  {passwordRules.hasUpperCase ? '✓' : '○'} One uppercase letter (A-Z)
                </div>
                <div style={{ color: passwordRules.hasLowerCase ? '#22c55e' : '#94a3b8' }}>
                  {passwordRules.hasLowerCase ? '✓' : '○'} One lowercase letter (a-z)
                </div>
                <div style={{ color: passwordRules.hasNumber ? '#22c55e' : '#94a3b8' }}>
                  {passwordRules.hasNumber ? '✓' : '○'} One number (0-9)
                </div>
                <div style={{ color: passwordRules.hasSpecialChar ? '#22c55e' : '#94a3b8' }}>
                  {passwordRules.hasSpecialChar ? '✓' : '○'} One special character (!@#$%^&*)
                </div>
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              margin: '20px 0',
              minHeight: '78px',
              alignItems: 'center',
              border: '2px dashed #a0e7ff',
              borderRadius: '8px',
              padding: '10px',
              backgroundColor: '#f0f9ff'
            }}>
              <div ref={captchaRef} className="h-captcha"></div>
              {!hcaptchaScriptLoaded && (
                <div style={{ color: '#0088cc', fontSize: '14px' }}>
                  Loading CAPTCHA...
                </div>
              )}
            </div>

            {!captchaToken && (
              <div style={{
                textAlign: 'center',
                color: '#0088cc',
                fontSize: '14px',
                marginBottom: '10px',
                fontWeight: 'bold'
              }}>
                ⬆️ Please complete the CAPTCHA above
              </div>
            )}


<button style={s.btn} onClick={handleSignup} disabled={loading}>Sign Up 🌟</button>
            <p>Already have account? <span onClick={() => setIsSignup(false)} style={s.link}>Login</span></p>
          </>
        )}



                {message && <div style={{ marginTop: '20px', fontWeight: 'bold', color: '#0088cc' }}>{message}</div>}
                {showResend &&
                  (
                    <button style={s.btn} onClick={handleResendConfirmation} disabled={loading || resendCooldown}>
                       {resendCooldown ? 'Please wait 60s...' : 'Resend confirmation email'}
                    </button>
                  )
                }


                {/* ── Footer — policy links (required for Paddle domain approval) ── */}
        <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e5e5', fontSize: '12px', color: '#94a3b8' }}>
          <a href="https://quantumaishield.com/tos.html" target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none' }}>Terms of Service</a>
          {' · '}
          <a href="https://quantumaishield.com/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none' }}>Privacy Policy</a>
          {' · '}
          <a href="https://quantumaishield.com/refund-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none' }}>Refund Policy</a>
          {' · '}
          <a href="https://quantumaishield.com/security-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#94a3b8', textDecoration: 'none' }}>Security Policy</a>
        </div>

      </div>
    </div>
  );
}

const s = {
          input: {
            width: '100%',
            padding: '14px',
            margin: '10px 0',
            borderRadius: '12px',
            border: '2px solid #cbd5e1',
            fontSize: '16px',
            outline: 'none',
            transition: 'border 0.2s',
            '&:focus': { borderColor: '#3b82f6' } // Note: inline style doesn't support pseudo-classes; we'll handle via className or keep simple
          },
  btn: { width: '100%', padding: '14px', margin: '15px 0', border: 'none', borderRadius: '12px', background: 'linear-gradient(to right, #00ccff, #0077ff)', color: 'white', fontSize: 'clamp(11px, 1.1vw, 18px)', fontWeight: 'bold', cursor: 'pointer' },
  link: { color: '#0066aa', cursor: 'pointer', textDecoration: 'underline' }
};

function App() {
  const [user, setUser] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [currentPage, setCurrentPage] = useState('welcome');
  const [previousPage, setPreviousPage] = useState(null);
  const [isPasswordReset, setIsPasswordReset] = useState(false);
  const [authLoading, setAuthLoading] = useState(true); // ✅ added to prevent flash
  // Controls the shared UpgradeScreen overlay opened from TrialBanner's
  // "Upgrade" button — kept separate from currentPage so opening/closing it
  // never changes which page the user returns to underneath.
  const [showUpgradeScreen, setShowUpgradeScreen] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);

  const [showStartGuide, setShowStartGuide] = useState(false);
  const [activitySessionId, setActivitySessionId] = useState(null);

  // ── Sequential step gating ──────────────────────────────────────────────
  // Dashboard → Risk Score → EU Classifier → Checklist → Documents must be
  // completed in order. Mapper and AI Assistant are intentionally excluded
  // from this list — they're always open regardless of progress.
  const STEP_SEQUENCE = ['dashboard', 'risk', 'euclassifier', 'checklist', 'documents'];
  const STEP_LABELS = { dashboard: 'Profile', risk: 'Risk Score', euclassifier: 'EU Classifier', checklist: 'Checklist', documents: 'Documents' };
  const [stepDone, setStepDone] = useState({});

  useEffect(() => {
    const checkSteps = () => {
      try {
        setStepDone({
          dashboard: !!localStorage.getItem(userKey('qais_onboarding')),
          risk: !!localStorage.getItem(userKey('qais_risk_result')),
          euclassifier:
            !!localStorage.getItem(userKey('qais_eu_classifier_result')) ||
            !!localStorage.getItem(userKey('qais_eu_classifier_skipped')) ||
            !!localStorage.getItem(userKey('qais_eu_advisory_result')) ||
            !!localStorage.getItem(userKey('qais_eu_nexus_result')),
          checklist: !!localStorage.getItem(userKey('qais_checklist_setup')),
          documents: parseInt(localStorage.getItem(userKey('qais_docs_generated')) || '0', 10) > 0,
        });
      } catch {
        setStepDone({});
      }
    };
    checkSteps();
    window.addEventListener('focus', checkSteps);
    window.addEventListener('qais-journey-update', checkSteps);
    return () => {
      window.removeEventListener('focus', checkSteps);
      window.removeEventListener('qais-journey-update', checkSteps);
    };
  }, [user]);

  const isStepUnlocked = (page) => {
    const idx = STEP_SEQUENCE.indexOf(page);
    if (idx <= 0) return true;
    for (let i = 0; i < idx; i++) {
      if (!stepDone[STEP_SEQUENCE[i]]) return false;
    }
    return true;
  };


  useEffect(() => {
    const checkProfile = () => {

      try {
        setHasProfile(!!localStorage.getItem(userKey('qais_onboarding')));
      } catch {
        setHasProfile(false);
      }
    };
    checkProfile();
    window.addEventListener('focus', checkProfile);
    window.addEventListener('qais-journey-update', checkProfile);
    return () => {
      window.removeEventListener('focus', checkProfile);
      window.removeEventListener('qais-journey-update', checkProfile);
    };
  }, [user]);



// ── Secret admin keyboard shortcut — Ctrl+Shift+A ────────────────────────
  // No link in UI — only accessible via keyboard shortcut
  // Only works if logged in as admin email
  useEffect(() => {
    function handleAdminShortcut(e) {
      console.log('[admin shortcut]', e.ctrlKey, e.shiftKey, e.key);
        if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'q') {
        const adminEmails = ['karan@quantumaishield.com', 'karancxo@gmail.com'];
        console.log('[admin] user email:', user?.email);
        console.log('[admin] includes check:', adminEmails.includes(user?.email?.toLowerCase()));
        if (user?.email && adminEmails.includes(user.email.toLowerCase())) {
          console.log('[admin] navigating to admin page');
          setCurrentPage('admin');
          console.log('[admin] user object:', JSON.stringify(user?.email));
          console.log('[admin] full user keys:', Object.keys(user || {}));
        }
      }
    }
    window.addEventListener('keydown', handleAdminShortcut);
    return () => window.removeEventListener('keydown', handleAdminShortcut);
  }, [user]);




  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');

    if (type === 'recovery') {
      setIsPasswordReset(true);
    }
  }, []);

  useEffect(() => {
    const hasCleanedUp = sessionStorage.getItem('app_initialized');
    if (!hasCleanedUp) {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('sb-')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      sessionStorage.setItem('app_initialized', 'true');
    }
  }, []);

        useEffect(() => {
    // Single auth listener — handles all auth state changes
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const currentUser = session?.user ?? null;
      setUser(currentUser);

  if (_event === 'SIGNED_IN' && currentUser) {
      initUserEmail().then(async email => {
        if (email) {
          migrateLegacyKeys(email);
          await syncFromServer();   // Pull server data + push any local-only data
          startSync();              // Begin 15-second background sync
        }
      });
      setShowWelcome(true);
      setCurrentPage('welcome');

      // ── Activity tracking: log this login (IP/browser/OS captured server-side) ──
      if (session?.access_token) {
       fetch(API_URL + '/api/activity?action=log-session', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
        })
          .then(r => r.json())
          .then(data => { if (data.success) setActivitySessionId(data.session_id); })
          .catch(err => console.error('[activity] log-session failed:', err.message));
      }
    }

    if (_event === 'SIGNED_OUT') {
        stopSync();                 // Stop background sync
        try { localStorage.removeItem('qais_user_email'); } catch {}
        setShowWelcome(false);
        setCurrentPage('welcome');
        setActivitySessionId(null);
    }
      setAuthLoading(false);
    });

    // Check for existing session on mount (page refresh)
    supabase.auth.getSession().then(({ data }) => {
      const currentUser = data.session?.user ?? null;
      setUser(currentUser);
      if (currentUser) {
        initUserEmail().then(email => {
          if (email) migrateLegacyKeys(email);
        });
        setShowWelcome(true);
        setCurrentPage('welcome');
      }
      setAuthLoading(false);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  // Tracks the current page without forcing the heartbeat interval below to
  // restart on every navigation -- a ref updates silently, state would not.
  const currentPageRef = React.useRef(currentPage);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  // ── Activity tracking: ping every 60s while this tab is visible, so
  // session duration is measurable even without a clean logout ──
  useEffect(() => {
    if (!activitySessionId) return;



    const sendHeartbeat = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) return;

        await fetch(API_URL + '/api/activity?action=heartbeat', {
          method: 'POST',
          headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: activitySessionId, current_page: currentPageRef.current }),
        });


      } catch (err) {
        console.error('[activity] heartbeat failed:', err.message);
      }
    };
    const intervalId = setInterval(sendHeartbeat, 60000);
    return () => clearInterval(intervalId);
  }, [activitySessionId]);

  const handleWelcomeComplete = async () => {
    setShowStartGuide(true);
    };

  const handleNavigation = (page) => {
    if (STEP_SEQUENCE.includes(page) && !isStepUnlocked(page)) {
      const idx = STEP_SEQUENCE.indexOf(page);
      const missing = STEP_SEQUENCE.slice(0, idx).find(p => !stepDone[p]);
      alert(`Please complete "${STEP_LABELS[missing]}" first before moving to this step.`);
      return;
    }
    setShowWelcome(false);
    setCurrentPage(page);
  };

  const handleLogout = async () => {
    // ── Activity tracking: log logout while the token is still valid —
    // signOut() invalidates it, so this must run first ──
    if (activitySessionId) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          await fetch(API_URL + '/api/activity?action=log-logout', {
            method: 'POST',
            headers: { 'Authorization': 'Bearer ' + session.access_token, 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: activitySessionId }),
          });
        }
      } catch (err) {
        console.error('[activity] log-logout failed:', err.message);
      }
    }

    // Don't clearUserKeys() — keep this user's data so they see it again on next login.
    // The email pointer is removed by the SIGNED_OUT handler.
    await supabase.auth.signOut();
    setUser(null);
    setShowWelcome(false);
    setCurrentPage('welcome');
    window.location.reload();
  };

  if (isPasswordReset) {
    return <LoginPage onLogin={setUser} />;
  }

  // ✅ Show loading spinner while auth is being determined
  if (authLoading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8fafc' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⏳</div>
          <p style={{ color: '#64748b', fontSize: 'clamp(11px, 1.1vw, 18px)' }}>Loading your compliance profile...</p>
        </div>
      </div>
    );
  }

if (!user) {
    return <LoginPage onLogin={setUser} />;
  }

  // ============================================================
  // TRIAL EXPIRY CHECK — hard-block expired users
  // ============================================================
  const accountStatus = getAccountStatus(user);
  if (accountStatus.status === 'expired') {
    return <UpgradeScreen onLogout={handleLogout} userEmail={user.email} />;
  }

  // Persistent upgrade access — shown for the ENTIRE trial (both 'active'
  // and 'warning'), not just the final 7-day warning window, so users can
  // purchase anytime instead of only when the trial is about to end or has
  // already ended. TrialBanner switches between a full urgency bar
  // (isWarning=true) and a compact, low-key persistent "Upgrade" button
  // (isWarning=false) internally — no behavior change during the warning
  // window itself.
  const trialBanner = (accountStatus.status === 'warning' || accountStatus.status === 'active') && (
    <TrialBanner
      daysLeft={accountStatus.daysLeft}
      expiresAt={accountStatus.expiresAt}
      isWarning={accountStatus.status === 'warning'}
      onUpgradeClick={() => setShowUpgradeScreen(true)}
    />
  );

  // Single shared purchase screen — same UpgradeScreen used post-expiry,
  // opened voluntarily from TrialBanner during an active trial. Rendered as
  // an overlay (not a page navigation), so closing it returns the user to
  // exactly the page/currentPage they were already on underneath.\

  // Simple, non-interactive "Start Here" overview — 5 short lines, one button.
  // No multi-step wizard, no forced tour — just a quick orientation new users
  // can skip or act on immediately.
  const startGuideOverlay = showStartGuide && (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ backgroundColor: '#0f172a', border: '2px solid #22d3ee', borderRadius: '16px', padding: '28px', maxWidth: '420px', width: '100%' }}>
        <h2 style={{ color: '#22d3ee', fontSize: '20px', margin: '0 0 4px' }}>🚀 Start Here Guide</h2>
        <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 4px' }}>5 quick steps to get set up — about 10 minutes total.</p>
        <p style={{ color: '#94a3b8', fontSize: '12px', margin: '0 0 18px' }}>Complete these in order — each step unlocks the next.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '22px' }}>
          {[
            ['👤', 'Complete your profile — Dashboard', '~2 min'],
            ['⚖️', 'Get your risk score — Risk Score', '~2 min'],
            ['🇪🇺', 'Classify with EU AI Act — EU Classifier', '~1 min'],
            ['✏️', 'Build your checklist — Checklist', '~1 min'],
            ['📄', 'Generate your documents — Documents', '~2 min'],
          ].map(([icon, label, time], i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'white', fontSize: '14px' }}>
              <span style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#1e293b', border: '1px solid #334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>{i + 1}</span>
              <span>{icon}</span>
              <span style={{ flex: 1 }}>{label}</span>
              <span style={{ color: '#64748b', fontSize: '11px' }}>{time}</span>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setShowStartGuide(false)} style={{ flex: 1, padding: '10px', backgroundColor: 'transparent', color: '#94a3b8', border: '2px solid #334155', borderRadius: '10px', cursor: 'pointer', fontSize: '13px' }}>
            Skip for now
          </button>
          <button
            onClick={() => { setShowStartGuide(false); setShowWelcome(false); setCurrentPage('dashboard'); }}
            style={{ flex: 1, padding: '10px', backgroundColor: '#22d3ee', color: '#0f172a', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}
          >
            Let's Go →
          </button>
        </div>
      </div>
    </div>
  );

      const upgradeScreenOverlay = showUpgradeScreen && (





    <div style={{ position: 'fixed', inset: 0, zIndex: 500, overflowY: 'auto' }}>
      <UpgradeScreen
        onClose={() => setShowUpgradeScreen(false)}
        onLogout={handleLogout}
        userEmail={user.email}
        isWarning={accountStatus.status === 'warning'}
        daysLeft={accountStatus.daysLeft}
        expiresAt={accountStatus.expiresAt}
      />
    </div>
  );

  // ============================================================
  // ADMIN PANEL -- renders before any other block
  // Double-gated: page check here + email check inside AdminPanel
  // ============================================================
if (currentPage === 'admin') {
    return <AdminPanel user={user} onNavigate={(page) => { setShowWelcome(true); setCurrentPage(page); }} />;
  }

  // ============================================================
  // WELCOME SCREEN NAVBAR (showWelcome = true)
  // ============================================================
    if (showWelcome) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
        {trialBanner}
        {upgradeScreenOverlay}
        {startGuideOverlay}
        {/* Navbar container */}
        <div style={{
          backgroundColor: '#0f172a',
          padding: '20px 40px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 100
        }}>
          {/* TOP ROW: Logo + title on left, Settings + Logout on right */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            {/* Logo and title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img src="/logo2.png" alt="Quantum AI Shield" style={{ width: '70px', height: '70px', objectFit: 'contain', mixBlendMode: 'screen' }} />

              <div>
                <h1 style={{
                  display: 'inline-block',
                  margin: 0,
                  fontSize: '28px',
                  fontWeight: 'bold',
                  backgroundImage: 'linear-gradient(135deg, #00D9FF 0%, #7B2CBF 100%)',
                  WebkitBackgroundClip: 'text',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  color: 'transparent'
                }}>
                  Quantum AI Shield<sup style={{ color: '#06B6D4', WebkitTextFillColor: '#06B6D4', fontSize: '14px' }}>™</sup>
                </h1>
                <p style={{ margin: 0, fontSize: '13px', fontStyle: 'italic', color: '#06b6d4' }}>
                  "Harvested Today. Unreadable for Decades."
                </p>
                <p style={{ margin: 0, fontSize: '24px', color: '#06b6d4' }}>
                  AI GRC Platform with Quantum Safe, Tamper-Evident Audit Trail for Every AI Decision
                </p>
              </div>

            </div>

            {/* Settings & Logout buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              {user && user.email && ['karan@quantumaishield.com','karancxo@gmail.com'].includes(user.email.toLowerCase()) && (
                <button
                  onClick={() => setCurrentPage('admin')}
                  title="Admin Panel"
                  style={{ padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 14px)', backgroundColor: '#dc2626', color: 'white', border: '2px solid #dc2626', borderRadius: '10px', cursor: 'pointer', fontSize: 'clamp(11px, 1.1vw, 18px)', fontWeight: 'bold' }}
                >
                  ADMIN
                </button>
              )}
              {!hasProfile && (
                <button
                  onClick={() => setShowStartGuide(true)}
                  title="Get started with your compliance profile"
                  style={{
                    padding: 'clamp(6px, 0.8vw, 12px) clamp(14px, 1.6vw, 22px)',
                    backgroundColor: '#22d3ee',
                    color: '#0f172a',
                    border: '2px solid #22d3ee',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    fontSize: 'clamp(11px, 1.1vw, 18px)',
                    fontWeight: 'bold',
                    boxShadow: '0 0 12px rgba(34,211,238,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  🚀 Get Started
                </button>
              )}
              <button
                onClick={() => { setShowWelcome(false); setCurrentPage('settings'); }}
                title="Settings & API Keys"
                style={{
                  padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 14px)',
                  backgroundColor: currentPage === 'settings' ? '#06b6d4' : 'transparent',
                  color: 'white',
                  border: '2px solid #475569',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: 'clamp(11px, 1.1vw, 18px)',
                  transition: 'all 0.2s'
                }}
              >
                ⚙️
              </button>
              <button
                onClick={handleLogout}
                title="Logout"
                style={{
                  padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 14px)',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: '2px solid #475569',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: 'clamp(11px, 1.1vw, 18px)',
                  transition: 'all 0.2s'
                }}
              >
                ⏻
              </button>
            </div>
          </div>

          {/* BOTTOM ROW: All other navigation buttons */}
            <div style={{ display: 'flex', gap: 'clamp(4px, 0.6vw, 10px)', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
            <button
              onClick={() => handleNavigation('dashboard')}
              style={{
                padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid #06b6d4',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: 'clamp(11px, 1.1vw, 18px)',
                fontWeight: 'bold',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>📊</span>
              <span>Dashboard</span>
            </button>

            <button
              onClick={() => handleNavigation('risk')}
              style={{
                padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid #06b6d4',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: 'clamp(11px, 1.1vw, 18px)',
                fontWeight: 'bold',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>⚖️</span>
              <span>Risk Score</span>
            </button>

            <button
              onClick={() => handleNavigation('euclassifier')}
              style={{
                padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid #06b6d4',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: 'clamp(11px, 1.1vw, 18px)',
                fontWeight: 'bold',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
           <EUFlagIcon size={32} />
              <span>EU Classifier</span>
            </button>

            <button
              onClick={() => handleNavigation('checklist')}
              style={{
                padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid #06b6d4',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: 'clamp(11px, 1.1vw, 18px)',
                fontWeight: 'bold',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>✏️</span>
              <span>Checklist</span>
            </button>

            <button
              onClick={() => handleNavigation('documents')}
              style={{
                padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid #06b6d4',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: 'clamp(11px, 1.1vw, 18px)',
                fontWeight: 'bold',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>📄</span>
              <span>Documents</span>
            </button>

            <button
              onClick={() => handleNavigation('mapper')}
              style={{
                padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid #06b6d4',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: 'clamp(11px, 1.1vw, 18px)',
                fontWeight: 'bold',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>🔗</span>
              <span>Mapper</span>
            </button>

            <button
              onClick={() => handleNavigation('chatbot')}
              style={{
                padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
                backgroundColor: 'transparent',
                color: 'white',
                border: '2px solid #06b6d4',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: 'clamp(11px, 1.1vw, 18px)',
                fontWeight: 'bold',
                transition: 'all 0.3s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>🤖</span>
              <span>AI Assistant</span>
            </button>
          </div>
        </div>

        <WelcomeScreen onStart={handleWelcomeComplete} />
      </div>
    );
  }

  // ============================================================
  // MAIN APP NAVBAR (showWelcome = false)
  // ============================================================
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {trialBanner}
      {upgradeScreenOverlay}
      {startGuideOverlay}
      <div style={{
        backgroundColor: '#0f172a',
        padding: '20px 40px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        {/* TOP ROW: Logo + title on left, Settings + Logout on right */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
                    {/* Logo and title with welcome message */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '32px', lineHeight: 1, position: 'relative', top: '-4px' }}>🛡️</span>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <h1 style={{
                margin: 0,
                fontSize: '30px',
                fontWeight: 'bold',
                textAlign: 'center',
                backgroundImage: 'linear-gradient(135deg, #00D9FF 0%, #7B2CBF 100%)',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                color: 'transparent'
              }}>
                Quantum AI Shield<sup style={{ color: '#06B6D4', WebkitTextFillColor: '#06B6D4', fontSize: '18px' }}>™</sup>
              </h1>
              <span style={{
                position: 'absolute',
                top: '50%',
                left: '100%',
                transform: 'translateY(-50%)',
                marginLeft: '14px',
                whiteSpace: 'nowrap',
                fontSize: '14px',
                color: '#94a3b8'
              }}>
                Welcome, {user.user_metadata.username || user.email.split('@')[0]}!
              </span>
              <p style={{ margin: '8px 0 0 0', fontSize: '13px', fontStyle: 'italic', color: '#06b6d4', textAlign: 'center' }}>
                "Harvested Today. Unreadable for Decades."
              </p>
            </div>
          </div>

      {/* Settings & Logout buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            {user && user.email && ['karan@quantumaishield.com','karancxo@gmail.com'].includes(user.email.toLowerCase()) && (
              <button
                onClick={() => setCurrentPage('admin')}
                title="Admin Panel"
                style={{ padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 14px)', backgroundColor: '#dc2626', color: 'white', border: '2px solid #dc2626', borderRadius: '10px', cursor: 'pointer', fontSize: 'clamp(11px, 1.1vw, 18px)', fontWeight: 'bold' }}
              >
                ADMIN
              </button>
            )}

            {!hasProfile && (
              <button
                onClick={() => setShowStartGuide(true)}
                title="Get started with your compliance profile"
                style={{
                  padding: 'clamp(6px, 0.8vw, 12px) clamp(14px, 1.6vw, 22px)',
                  backgroundColor: '#22d3ee',
                  color: '#0f172a',
                  border: '2px solid #22d3ee',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: 'clamp(11px, 1.1vw, 18px)',
                  fontWeight: 'bold',
                  boxShadow: '0 0 12px rgba(34,211,238,0.6)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap',
                }}
              >
                🚀 Get Started
              </button>
            )}

            <button
              onClick={() => setCurrentPage('settings')}
              title="Settings & API Keys"


              style={{
                padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 14px)',
                backgroundColor: currentPage === 'settings' ? '#06b6d4' : 'transparent',
                color: 'white',
                border: '2px solid #475569',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: 'clamp(11px, 1.1vw, 18px)',
                transition: 'all 0.2s'
              }}
            >
              ⚙️
            </button>
            <button
              onClick={handleLogout}
              title="Logout"
              style={{
                padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 14px)',
                backgroundColor: '#ef4444',
                color: 'white',
                border: '2px solid #475569',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: 'clamp(11px, 1.1vw, 18px)',
                transition: 'all 0.2s'
              }}
            >
              ⏻
            </button>
          </div>
        </div>

        {/* BOTTOM ROW: All other navigation buttons */}
          <div style={{ display: 'flex', gap: 'clamp(4px, 0.6vw, 10px)', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center' }}>
          <button
            onClick={() => setCurrentPage('dashboard')}
            style={{
              padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
              backgroundColor: currentPage === 'dashboard' ? '#06b6d4' : 'transparent',
              color: 'white',
              border: '2px solid #06b6d4',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: 'clamp(11px, 1.1vw, 18px)',
              fontWeight: 'bold',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>📊</span>
            <span>Dashboard</span>
          </button>

          <button
            onClick={() => setCurrentPage('risk')}
            style={{
              padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
              backgroundColor: currentPage === 'risk' ? '#06b6d4' : 'transparent',
              color: 'white',
              border: '2px solid #06b6d4',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: 'clamp(11px, 1.1vw, 18px)',
              fontWeight: 'bold',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>⚖️</span>
            <span>Risk Score</span>
          </button>

          <button
            onClick={() => setCurrentPage('euclassifier')}
            style={{
              padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
              backgroundColor: currentPage === 'euclassifier' ? '#06b6d4' : 'transparent',
              color: 'white',
              border: '2px solid #06b6d4',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: 'clamp(11px, 1.1vw, 18px)',
              fontWeight: 'bold',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <EUFlagIcon size={32} />
            <span>EU Classifier</span>
          </button>

          <button
            onClick={() => setCurrentPage('checklist')}
            style={{
              padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
              backgroundColor: currentPage === 'checklist' ? '#06b6d4' : 'transparent',
              color: 'white',
              border: '2px solid #06b6d4',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: 'clamp(11px, 1.1vw, 18px)',
              fontWeight: 'bold',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>✏️</span>
            <span>Checklist</span>
          </button>

          <button
            onClick={() => setCurrentPage('documents')}
            style={{
              padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
              backgroundColor: currentPage === 'documents' ? '#06b6d4' : 'transparent',
              color: 'white',
              border: '2px solid #06b6d4',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: 'clamp(11px, 1.1vw, 18px)',
              fontWeight: 'bold',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>📄</span>
            <span>Documents</span>
          </button>

          <button
            onClick={() => setCurrentPage('mapper')}
            style={{
              padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
              backgroundColor: currentPage === 'mapper' ? '#06b6d4' : 'transparent',
              color: 'white',
              border: '2px solid #06b6d4',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: 'clamp(11px, 1.1vw, 18px)',
              fontWeight: 'bold',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>🔗</span>
            <span>Mapper</span>
          </button>

          <button
            onClick={() => setCurrentPage('chatbot')}
            style={{
              padding: 'clamp(6px, 0.8vw, 12px) clamp(8px, 1.2vw, 20px)',
              backgroundColor: currentPage === 'chatbot' ? '#06b6d4' : 'transparent',
              color: 'white',
              border: '2px solid #06b6d4',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: 'clamp(11px, 1.1vw, 18px)',
              fontWeight: 'bold',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>🤖</span>
            <span>AI Assistant</span>
          </button>
        </div>
      </div>

      <ProgressJourneyBar currentPage={currentPage} user={user} onNavigate={(page) => { setPreviousPage(currentPage); setCurrentPage(page); }} />

      <OnboardingTooltip page={currentPage} />

      <div style={{ padding: '20px' }}>
        {currentPage === 'welcome' && <WelcomeScreen onStart={() => setCurrentPage('dashboard')} />}
        {currentPage === 'dashboard' && <Dashboard />}
        {currentPage === 'chatbot' && <ComplianceChatbot />}
        {currentPage === 'documents' && <DocumentGenerator />}
        {currentPage === 'mapper' && <CrossFrameworkMapper />}
        {currentPage === 'risk' && <RiskCalculator onNavigate={(page) => { setPreviousPage(currentPage); setCurrentPage(page); }} returnTo={previousPage === 'checklist' ? 'checklist' : null} />}
        {currentPage === 'euclassifier' && <EUAIActClassifier />}
        {currentPage === 'checklist' && <ChecklistBuilder onNavigate={setCurrentPage} />}
        {currentPage === 'settings' && <SettingsPage user={user} />}
        {currentPage === 'admin' && <AdminPanel user={user} onNavigate={setCurrentPage} />}
      </div>

      <div style={{
        backgroundColor: '#0f172a',
        color: '#94a3b8',
        textAlign: 'center',
        padding: '30px',
        marginTop: '60px'
      }}>

        <p style={{ margin: '5px 0 0 0', fontSize: '12px' }}>
          www.QuantumAIShield.com
        </p>
      </div>
    </div>
  );
}

export default App;